import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, Loader2, Star, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { userInterestsApi } from '../lib/api';

export interface ContactInfo {
  id: string;
  name: string;
  gender: string;
  birthdate: string;
}

export interface MonthlyRankingEntry {
  contact: ContactInfo;
  receivedCount: number;
}

export const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const getMedalColor = (position: number) => {
  if (position === 0) return 'text-amber-500';
  if (position === 1) return 'text-gray-400';
  if (position === 2) return 'text-amber-700';
  return 'text-gray-300';
};

export const RankingColumn = ({ title, color, entries, loading }: {
  title: string;
  color: 'rose' | 'sky';
  entries: MonthlyRankingEntry[];
  loading: boolean;
}) => {
  const bgGradient = color === 'rose'
    ? 'from-rose-500 to-pink-500'
    : 'from-sky-500 to-blue-500';
  const badgeColor = color === 'rose'
    ? 'bg-rose-100 text-rose-700'
    : 'bg-sky-100 text-sky-700';
  const starColor = color === 'rose'
    ? 'fill-rose-400 text-rose-400'
    : 'fill-sky-400 text-sky-400';
  const numberBg = color === 'rose'
    ? 'bg-rose-50 text-rose-600'
    : 'bg-sky-50 text-sky-600';
  const emptyIcon = color === 'rose'
    ? 'text-rose-200'
    : 'text-sky-200';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className={`bg-gradient-to-r ${bgGradient} px-4 sm:px-5 py-3`}>
        <h3 className="font-semibold text-white flex items-center gap-2 text-sm sm:text-base">
          <Trophy className="h-4 w-4 sm:h-5 sm:w-5" />
          {title}
        </h3>
      </div>
      {loading ? (
        <div className="p-8 flex justify-center">
          <Loader2 className={`h-6 w-6 animate-spin ${color === 'rose' ? 'text-rose-400' : 'text-sky-400'}`} />
        </div>
      ) : entries.length > 0 ? (
        <div className="divide-y divide-gray-50">
          {entries.map((entry, index) => (
            <div
              key={entry.contact.id}
              className="flex items-center gap-3 px-4 sm:px-5 py-2.5 hover:bg-gray-50/50 transition-colors"
            >
              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                {index < 3 ? (
                  <Trophy className={`h-5 w-5 ${getMedalColor(index)}`} />
                ) : (
                  <span className={`text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center ${numberBg}`}>
                    {index + 1}
                  </span>
                )}
              </div>
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {entry.contact.name}
              </span>
              <span className={`inline-flex items-center gap-1 text-sm font-semibold ${badgeColor} px-2 py-0.5 rounded-full`}>
                <Star className={`h-3.5 w-3.5 ${starColor}`} />
                {entry.receivedCount}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center">
          <Trophy className={`h-10 w-10 mx-auto mb-2 ${emptyIcon}`} />
          <p className="text-gray-400 text-sm">Nessuna preferenza</p>
        </div>
      )}
    </div>
  );
};

const MonthlyRanking = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [womenEteroRanking, setWomenEteroRanking] = useState<MonthlyRankingEntry[]>([]);
  const [menEteroRanking, setMenEteroRanking] = useState<MonthlyRankingEntry[]>([]);
  const [womenGayRanking, setWomenGayRanking] = useState<MonthlyRankingEntry[]>([]);
  const [menGayRanking, setMenGayRanking] = useState<MonthlyRankingEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMonthlyData();
  }, [selectedMonth, selectedYear]);

  const loadMonthlyData = async () => {
    try {
      setLoading(true);
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString();
      const endDate = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59).toISOString();

      const data = await userInterestsApi.getByDateRange(startDate, endDate);

      const receivedMap = new Map<string, number>();
      const contactMap = new Map<string, ContactInfo>();

      (data || []).forEach((interest: any) => {
        if (interest.to_user) {
          const toId = interest.to_user.id;
          contactMap.set(toId, interest.to_user);
          receivedMap.set(toId, (receivedMap.get(toId) || 0) + 1);
        }
      });

      const allEntries: MonthlyRankingEntry[] = Array.from(contactMap.entries()).map(([id, contact]) => ({
        contact,
        receivedCount: receivedMap.get(id) || 0,
      }));

      const sortDesc = (a: MonthlyRankingEntry, b: MonthlyRankingEntry) => b.receivedCount - a.receivedCount;

      setWomenEteroRanking(allEntries.filter(e => e.contact.gender === 'donna_etero').sort(sortDesc));
      setMenEteroRanking(allEntries.filter(e => e.contact.gender === 'uomo_etero').sort(sortDesc));
      setWomenGayRanking(allEntries.filter(e => e.contact.gender === 'donna_gay').sort(sortDesc));
      setMenGayRanking(allEntries.filter(e => e.contact.gender === 'uomo_gay').sort(sortDesc));
    } catch (err) {
      console.error('Error loading monthly ranking:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  const isCurrentMonth = selectedMonth === now.getMonth() && selectedYear === now.getFullYear();

  const yearOptions: number[] = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2 text-lg uppercase">
              <Calendar className="h-5 w-5 text-rose-500" />
              Classifica Mensile Italia
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {MONTHS_IT.map((month, idx) => (
                      <option key={idx} value={idx}>{month}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative">
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <button
                onClick={goToNextMonth}
                disabled={isCurrentMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RankingColumn
          title="Classifica Donne Etero"
          color="rose"
          entries={womenEteroRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Uomini Etero"
          color="sky"
          entries={menEteroRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Donne Gay"
          color="rose"
          entries={womenGayRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Uomini Gay"
          color="sky"
          entries={menGayRanking}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default MonthlyRanking;
